import { useEffect, useContext, useRef, useLayoutEffect, useState } from 'react'
import { useSelector, shallowEqual } from 'react-redux'
import PropTypes from 'prop-types'
import { createUseStyles } from 'react-jss'
import gsap from 'gsap'
import ally from 'ally.js'
import { Context } from '@/context'
import DelayLink from '@/components/DelayLink'
import usePrevious from '@/hooks/usePrevious'
import { decodeEntities } from '@/utils/decodeEntities'
import style from './style'

const useStyles = createUseStyles(style)

const Nav = ({
  mainNav,
  extraNav,
}) => {
  const { isNavOpen, setNavOpen, headerHeight, headerRef, cookieRef } = useContext(Context)
  const classes = useStyles({ headerHeight, isNavOpen })
  const $mainNav = useRef()
  const $extraNav = useRef()
  const $footer = useRef()
  const { pathname, strings } = useSelector((state) => ({
    pathname: state.router.location.pathname,
    mediaQueryKey: state.mediaQuery.key,
    strings: state.language.string,
  }), shallowEqual)
  const prevPathname = usePrevious(pathname)

  const $root = useRef()
  const disableAllTabIndex = useRef(null)

  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (mainNav.length && extraNav.length) setReady(true)
  }, [mainNav, extraNav])

  /*------------------------------
  Initialize
  ------------------------------*/
  const init = () => {
    gsap.set($root.current, { autoAlpha: 0 })
    gsap.set($mainNav.current, { autoAlpha: 0 })
    gsap.set($extraNav.current, { autoAlpha: 0 })
    gsap.set($footer.current, { autoAlpha: 0 })
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && e.keyCode === 27 && isNavOpen) setNavOpen(false)
  }

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isNavOpen])

  const openMainMenu = () => {
    gsap.killTweensOf([$root.current, $mainNav.current.children, $extraNav.current.children, $footer.current])
    const tl = gsap.timeline({ ease: 'power3.out' })
    tl
      .to($root.current, {
        duration: 1,
        autoAlpha: 1,
      })
      .to([$mainNav.current, $extraNav.current], {
        autoAlpha: 1,
        stagger: 0.08,
      })
      .to($footer.current, {
        duration: 0.5,
        autoAlpha: 1,
      }, '<')
  }

  const closeMainMenu = () => {
    gsap.killTweensOf([$root.current, $mainNav.current.children, $extraNav.current.children, $footer.current])
    const tl = gsap.timeline({ ease: 'power3.out' })
    tl
      .to([$mainNav.current, $extraNav.current, $footer.current], {
        duration: 0.5,
        autoAlpha: 0,
      })
      .to($root.current, {
        duration: 0.5,
        autoAlpha: 0,
      })
  }

  /*------------------------------
  Init
  ------------------------------*/
  useEffect(() => {
    if (ready) init()
  }, [ready])

  useEffect(() => {
    if (isNavOpen) {
      disableAllTabIndex.current = ally.maintain.disabled({
        filter: [$root.current, headerRef.current, ...cookieRef.current ? [cookieRef.current] : []],
      })
    }
    if (!isNavOpen && disableAllTabIndex.current !== null) disableAllTabIndex.current.disengage()
  }, [isNavOpen])

  const prevIsOpen = usePrevious(isNavOpen)
  useEffect(() => {
    if (prevIsOpen !== undefined && isNavOpen) openMainMenu()
    if (prevIsOpen !== undefined && !isNavOpen) closeMainMenu()
  }, [isNavOpen])

  useLayoutEffect(() => {
    if (prevPathname !== pathname) {
      // Close Nav after old page came out
      setTimeout(() => {
        setNavOpen(false)
      }, 500)
    }
  }, [pathname])

  const renderFooter = () => {
    return (
      <div
        className={classes.wrapperFooterNav}
        ref={$footer}
      >
        <div>
          {strings.linkedin_label && (
            <DelayLink
              to={strings.linkedin_link}
              className={classes.linkedinBtn}
            >
              <svg>
                <title>{strings.linkedin_label}</title>
                <use xlinkHref="#ico-linkedin" />
              </svg>
            </DelayLink>
          )}
        </div>
        <div>
          {strings.privacy_policy_label && (
            <DelayLink to={strings.privacy_policy_link}>
              {strings.privacy_policy_label}
            </DelayLink>
          )}
        </div>
      </div>
    )
  }

  const renderMainNav = () => {
    return (
      <nav className={`${classes.nav} ${classes.mainNav}`}>
        <ul ref={$mainNav}>
          {mainNav.map((item) => (
            <li key={item.ID}>
              <DelayLink to={item.url}>
                {decodeEntities(item.title)}
              </DelayLink>
            </li>
          ))}
        </ul>
      </nav>
    )
  }

  const renderExtraNav = () => {
    return (
      <nav className={`${classes.nav} ${classes.extraNav}`}>
        <ul ref={$extraNav}>
          {extraNav.map((item) => (
            <li
              className={item.classes.join(' ')}
              key={item.ID}
            >
              <DelayLink to={item.url}>
                {decodeEntities(item.title)}
              </DelayLink>
            </li>
          ))}
        </ul>
      </nav>
    )
  }

  return ready && (
    <div
      className={classes.root}
      ref={$root}
      role="dialog"
      aria-modal="true"
    >
      <div className={classes.container}>
        <div className={classes.wrapper}>
          <div className={classes.wrapperMain}>
            <div className={classes.wrapperMainNav}>
              <div className={classes.wrapperTableNav}>
                {renderMainNav()}
                {renderExtraNav()}
              </div>
            </div>
          </div>
          {renderFooter()}
        </div>
      </div>
    </div>
  )
}

Nav.propTypes = {
  mainNav: PropTypes.array,
  extraNav: PropTypes.array,
}

Nav.defaultProps = {
  mainNav: [],
  extraNav: [],
}

export default Nav
